import cn from "classnames";
import { InputText, InputTextProps } from "primereact/inputtext";
import React, { useState } from "react";

import { getDataTestId } from "../../../../utils/getDataTestId";
import {
  detachLayoutProps,
  WSElement,
  WSElementCustomProps,
  WSElementLayoutProps
} from "../../WSElement/WSElement.component";
import { WSButton } from "../WSButton/WSButton.component";
import { WSIcon, WSIconName } from "../WSIcon/WSIcon.component";

import styles from "./WSTextInput.module.scss";

export interface WSTextInputProps
  extends Omit<InputTextProps, "pt" | "prefix" | "size" | "color">,
    WSElementLayoutProps,
    WSElementCustomProps {
  icon?: WSIconName;
  inputClassName?: string;
  prefix?: React.ReactNode;
  postfix?: React.ReactNode;
  allowToShowPassword?: boolean;
  error?: boolean;
}

export const WSTextInput = React.forwardRef<HTMLInputElement, WSTextInputProps>(
  (props, ref) => {
    const [passwordVisible, setPasswordVisible] = useState(false);

    const {
      icon,
      error,
      className,
      inputClassName: customInputClassName,
      type: initialType = "text",
      prefix,
      postfix,
      allowToShowPassword,
      systemProps,
      layoutProps,
      ...otherProps
    } = detachLayoutProps(props);

    const type =
      initialType === "password"
        ? passwordVisible
          ? "text"
          : "password"
        : initialType;
    const inputClassName = cn(styles.input, customInputClassName);
    const testIdBase = getDataTestId("textInput", otherProps.name);

    const containerClassName = cn(styles.inputContainer, className, {
      [styles.error]: error,
      [styles.postfix]: postfix,
      [styles.disabled]: otherProps.disabled
    });

    return (
      <WSElement
        {...systemProps}
        className={containerClassName}
        data-testid={testIdBase}
        {...layoutProps}
      >
        {icon && <WSIcon block name={icon} className={styles.icon} size="XS" />}

        {prefix ? (
          <WSElement className={styles.prefix}>{prefix}</WSElement>
        ) : null}

        <InputText
          ref={ref}
          {...otherProps}
          type={type}
          className={inputClassName}
          data-testid={`${testIdBase}Input`}
        />

        {initialType === "password" && allowToShowPassword ? (
          <WSButton.Link
            type="button"
            onClick={() => {
              setPasswordVisible((v) => !v);
            }}
          >
            {passwordVisible ? "Hide" : "Show"}
          </WSButton.Link>
        ) : null}

        {postfix ? (
          <WSElement className={styles.postfix}>{postfix}</WSElement>
        ) : null}
      </WSElement>
    );
  }
);

WSTextInput.displayName = "WSTextInput";
